import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  ShoppingBag,
  Store,
} from 'lucide-react'
import { applyProductSeo, clearProductSeo } from '../lib/productSeo'
import { formatRupiah, getImageUrl, getProductCatalogUrl } from '../lib/catalogUrl'
import { normalizePlanId, canUseFeature } from '../lib/plans'
import { trackProductView, trackWhatsAppClick } from '../hooks/useApi'

const API_URL = import.meta.env.VITE_API_URL || '/api'
const DEFAULT_THEME = '#3b82f6'

type PublicProductResponse = {
  product: {
    id: number
    name: string
    slug: string
    description?: string | null
    price: number | string
    discountPrice?: number | string | null
    images?: string[] | null
    brandId: number
  }
  brand: {
    id: number
    name: string
    slug: string
    logoUrl?: string | null
    whatsapp?: string | null
    colorTheme?: string | null
    plan?: string
    badge?: string | null
  }
  seoEnabled: boolean
  productUrl: string
  relatedProducts: {
    id: number
    name: string
    slug: string | null
    price: number | string
    discountPrice?: number | string | null
    images?: string[] | null
  }[]
}

const buildWaLink = (whatsapp: string, message: string) => {
  const phone = whatsapp.replace(/[^0-9]/g, '')
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
}

export default function ProductPage() {
  const { brandSlug, productSlug } = useParams()
  const [imageIndex, setImageIndex] = useState(0)

  const { data, isLoading, error } = useQuery<PublicProductResponse>({
    queryKey: ['product', 'public', brandSlug, productSlug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/products/public/${brandSlug}/${productSlug}`)
      if (!res.ok) throw new Error('Produk tidak ditemukan')
      return res.json()
    },
    enabled: !!brandSlug && !!productSlug,
  })

  const product = data?.product
  const brand = data?.brand
  const planId = normalizePlanId(brand?.plan)
  const themeColor =
    brand?.colorTheme && canUseFeature(planId, 'custom_theme')
      ? brand.colorTheme
      : DEFAULT_THEME

  useEffect(() => {
    if (product && brand && product.slug) {
      applyProductSeo(
        {
          name: product.name,
          description: product.description,
          images: product.images,
          price: product.price,
          discountPrice: product.discountPrice,
        },
        { name: brand.name, slug: brand.slug },
        product.slug,
        data?.seoEnabled ?? false
      )
      trackProductView(product.id)
    }
    return () => clearProductSeo()
  }, [product, brand, data?.seoEnabled])

  useEffect(() => {
    setImageIndex(0)
  }, [product?.id])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Memuat produk...</div>
      </div>
    )
  }

  if (error || !product || !brand) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <ShoppingBag className="w-16 h-16 text-gray-300 mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">Produk tidak ditemukan</h1>
        <p className="text-gray-500 mb-6 text-center">Produk mungkin tidak aktif atau URL salah.</p>
        {brandSlug && (
          <Link
            to={`/catalog/${brandSlug}`}
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali ke katalog
          </Link>
        )}
      </div>
    )
  }

  const images = product.images?.filter(Boolean) ?? []
  const price = Number(product.price) || 0
  const discount = product.discountPrice != null ? Number(product.discountPrice) : null
  const hasDiscount = discount != null && discount < price
  const waMessage = `Halo, saya tertarik dengan produk *${product.name}* dari ${brand.name}.\n\nHarga: ${formatRupiah(hasDiscount ? discount : price)}\n\nBisa info lebih lanjut?`
  const waLink = brand.whatsapp ? buildWaLink(brand.whatsapp, waMessage) : ''
  const catalogPath = `/catalog/${brand.slug}`
  const showProductLinks = data?.seoEnabled ?? false

  return (
    <div className="min-h-screen bg-[#f8f6f2]">
      {/* Navbar ringkas */}
      <header
        className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b shadow-sm"
        style={{ borderColor: `${themeColor}33` }}
      >
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link
            to={catalogPath}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 min-w-0"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            <span className="truncate">{brand.name}</span>
          </Link>
          {brand.badge && (
            <span
              className={`text-[10px] font-bold px-2 py-0.5 rounded shrink-0 ${
                brand.badge === 'Premium' ? 'bg-amber-500 text-white' : 'bg-blue-100 text-blue-700'
              }`}
            >
              {brand.badge}
            </span>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 sm:py-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid md:grid-cols-2 gap-8 lg:gap-12"
        >
          {/* Galeri */}
          <div>
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-lg">
              {images.length > 0 ? (
                <>
                  <img
                    src={getImageUrl(images[imageIndex]) || ''}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  {images.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setImageIndex((i) => (i === 0 ? images.length - 1 : i - 1))}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white"
                        aria-label="Gambar sebelumnya"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setImageIndex((i) => (i === images.length - 1 ? 0 : i + 1))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white"
                        aria-label="Gambar berikutnya"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {images.map((_, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setImageIndex(idx)}
                            className={`w-2 h-2 rounded-full transition-colors ${
                              idx === imageIndex ? 'bg-white' : 'bg-white/50'
                            }`}
                            aria-label={`Gambar ${idx + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                  <ShoppingBag size={80} style={{ color: themeColor }} className="opacity-40" />
                </div>
              )}
            </div>

            {images.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setImageIndex(idx)}
                    className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                      idx === imageIndex ? 'border-indigo-500' : 'border-gray-200'
                    }`}
                  >
                    <img src={getImageUrl(img) || ''} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Detail */}
          <div className="flex flex-col">
            <Link
              to={catalogPath}
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3"
            >
              <Store className="w-4 h-4" />
              {brand.name}
            </Link>

            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight mb-4">
              {product.name}
            </h1>

            <div className="mb-6">
              {hasDiscount ? (
                <div className="flex flex-wrap items-end gap-3">
                  <span className="text-3xl font-bold" style={{ color: themeColor }}>
                    {formatRupiah(discount)}
                  </span>
                  <span className="text-lg text-gray-400 line-through">{formatRupiah(price)}</span>
                  <span className="text-sm font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded">
                    -{Math.round((1 - discount! / price) * 100)}%
                  </span>
                </div>
              ) : (
                <span className="text-3xl font-bold" style={{ color: themeColor }}>
                  {formatRupiah(price)}
                </span>
              )}
            </div>

            {product.description && (
              <div className="prose prose-sm max-w-none text-gray-700 mb-8 whitespace-pre-wrap leading-relaxed">
                {product.description}
              </div>
            )}

            {waLink && (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => brand.id && trackWhatsAppClick(brand.id)}
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-xl text-white font-semibold text-lg shadow-lg hover:opacity-95 transition-opacity"
                style={{ backgroundColor: '#25D366' }}
              >
                <MessageCircle className="w-5 h-5" />
                Pesan via WhatsApp
              </a>
            )}
          </div>
        </motion.div>

        {/* Produk lainnya */}
        {data.relatedProducts.length > 0 && (
          <section className="mt-14 pt-10 border-t border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Produk Lainnya dari {brand.name}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {data.relatedProducts.map((item) => {
                const itemPrice = Number(item.discountPrice ?? item.price) || 0
                const cardInner = (
                  <>
                    <div className="aspect-square bg-gray-100 overflow-hidden">
                      {item.images?.[0] ? (
                        <img
                          src={getImageUrl(item.images[0]) || ''}
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="w-8 h-8 text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-semibold text-gray-900 line-clamp-2 min-h-[2.5rem]">
                        {item.name}
                      </p>
                      <p className="text-sm font-bold mt-1" style={{ color: themeColor }}>
                        {formatRupiah(itemPrice)}
                      </p>
                    </div>
                  </>
                )

                if (showProductLinks && item.slug) {
                  return (
                    <Link
                      key={item.id}
                      to={getProductCatalogUrl(brand.slug, item.slug)}
                      className="group block rounded-xl overflow-hidden border border-gray-200 bg-white hover:shadow-md transition-shadow"
                    >
                      {cardInner}
                    </Link>
                  )
                }

                return (
                  <div
                    key={item.id}
                    className="group rounded-xl overflow-hidden border border-gray-200 bg-white"
                  >
                    {cardInner}
                  </div>
                )
              })}
            </div>

            <div className="text-center mt-8">
              <Link
                to={catalogPath}
                className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium"
              >
                Lihat semua produk di katalog
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </Link>
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
